<template>
  <div class="crm-dashboard">
    <!-- Navigation Menu -->
    <v-card class="nav-menu-card" elevation="2">
      <div class="nav-menu">
        <router-link v-for="(item, index) in menuItems" :key="index" :to="item.route" class="nav-item"
          :class="{ 'active': isActiveRoute(item.route) }">
          <v-icon :color="isActiveRoute(item.route) ? APP_Color : '#718096'" size="20">
            {{ item.icon }}
          </v-icon>
          <span class="nav-label">{{ item.label }}</span>
        </router-link>
      </div>
    </v-card>

    <!-- Stats Cards -->
    <v-row class="stats-row" dense>
      <v-col v-for="(stat, index) in stats" :key="index" cols="12" sm="6" md="3">
        <v-card class="stat-card" :class="`stat-${stat.id || index}`" elevation="1" @click="$router.push(stat.route)"
          style="cursor: pointer;">
          <div class="stat-card-inner">
            <div class="stat-top-row">
              <div class="stat-icon-mini" :style="{ background: stat.bgColor }">
                <v-icon :color="stat.color" size="18">{{ stat.icon }}</v-icon>
              </div>
              <div v-if="stat.change && stat.change !== '+0%'" class="stat-trend-mini" :class="stat.trend">
                <v-icon size="12">{{ stat.trend === 'up' ? 'mdi-arrow-up' : stat.trend === 'down' ? 'mdi-arrow-down' :
                  'mdi-minus' }}</v-icon>
                <span>{{ stat.change.split(' ')[0] }}</span>
              </div>
            </div>

            <div class="stat-main-content">
              <div class="stat-value-mini">{{ stat.value }}</div>
              <div class="stat-label-mini">{{ stat.label }}</div>
            </div>

            <div v-if="stat.change && stat.change.includes('|')" class="stat-footer-mini">
              {{ stat.change.split('|')[1].trim() }}
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Row -->
    <v-row class="charts-row">
      <!-- Sales Pipeline -->
      <v-col cols="12" md="7">
        <v-card class="chart-card" elevation="2">
          <v-card-title class="card-title justify-space-between">
            <div class="d-flex align-center">
              <v-icon :color="APP_Color" size="24" class="mr-2">mdi-chart-line</v-icon>
              <span>Pipeline Vânzări</span>
            </div>

            <div class="chart-filters d-flex align-center">
              <DxDateBox v-model:value="pipelineStartDate" type="date" display-format="dd/MM/yyyy" width="115"
                class="premium-date-box" @value-changed="refreshPipeline" />
              <span class="filter-separator">-</span>
              <DxDateBox v-model:value="pipelineEndDate" type="date" display-format="dd/MM/yyyy" width="115"
                class="premium-date-box" @value-changed="refreshPipeline" />
            </div>
          </v-card-title>
          <v-card-text>
            <div class="pipeline-stages">
              <div v-for="(stage, index) in pipelineStages" :key="index" class="pipeline-stage">
                <div class="stage-header">
                  <span class="stage-name">{{ stage.name }}</span>
                  <span class="stage-count">{{ stage.count }}</span>
                </div>
                <div class="stage-bar">
                  <div class="stage-progress" :style="{
                    width: stage.percentage + '%',
                    background: stage.color
                  }"></div>
                </div>
                <div class="stage-value">{{ stage.value }} EUR</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Top Performers -->
      <v-col cols="12" md="5">
        <v-card class="activity-card" elevation="2">
          <v-card-title class="card-title">
            <v-icon :color="APP_Color" size="24" class="mr-2">mdi-clock-outline</v-icon>
            <span>Activități Recente</span>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-timeline side="end" density="compact" class="activity-timeline">
              <v-timeline-item v-for="(activity, index) in recentActivities" :key="index" :dot-color="activity.color"
                size="small">
                <div class="activity-content" :class="{ 'clickable-activity': activity.opportunityId }"
                  @click="activity.opportunityId ? goToOpportunity(activity.opportunityId) : null">
                  <div class="activity-header">

                    <span class="activity-time">{{ activity.time }}</span>
                  </div>

                  <div class="activity-footer d-flex align-center mt-1">
                    <v-chip v-if="activity.tag" size="x-small" :color="activity.tagColor" class="mr-2">
                      {{ activity.tag }}
                    </v-chip>
                    <span v-if="activity.opportunity" class="activity-opp-link"
                      @click="goToOpportunity(activity.opportunityId)">
                      <v-icon size="12" class="mr-1">mdi-briefcase-outline</v-icon>
                      {{ activity.opportunity }}
                    </span>
                  </div>
                  <div class="activity-description">{{ activity.description }}</div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Forecast Chart -->
    <v-row class="charts-row">
      <v-col cols="12">
        <v-card class="chart-card" elevation="2">
          <v-card-title class="card-title justify-space-between">
            <div class="d-flex align-center">
              <v-icon :color="APP_Color" size="24" class="mr-2">mdi-chart-bar</v-icon>
              <span>Prognoză Buget</span>
            </div>

            <div class="chart-filters d-flex align-center">
              <DxDateBox v-model:value="chartStartDate" type="date" display-format="dd/MM/yyyy" width="115"
                class="premium-date-box" @value-changed="refreshChart" />
              <span class="filter-separator">-</span>
              <DxDateBox v-model:value="chartEndDate" type="date" display-format="dd/MM/yyyy" width="115"
                class="premium-date-box" @value-changed="refreshChart" />
            </div>
          </v-card-title>
          <v-card-text>
            <DxChart :data-source="monthlySalesData" :rotated="false" title="">
              <DxArgumentAxis>
                <DxLabel :rotated="true" rotation-angle="45" />
              </DxArgumentAxis>

              <DxSeries argument-field="month" value-field="value" name="Valoare Ponderată" type="bar"
                color="#9C27B0" />

              <DxValueAxis>
                <DxLabel :visible="true" />
              </DxValueAxis>

              <DxLegend :visible="false" />

              <DxTooltip :enabled="true" :shared="true" :customize-tooltip="(arg) => {
                return {
                  text: arg.argumentText + ': ' + formatCurrency(arg.originalValue)
                };
              }" />
            </DxChart>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="bottom-row">
      <v-col cols="12">
        <v-card class="activity-card" elevation="2">
          <v-card-title class="card-title">
            <v-icon :color="APP_Color" size="24" class="mr-2">mdi-briefcase-outline</v-icon>
            <span>Oportunități Noi</span>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-table class="dashboard-table">
              <thead>
                <tr>
                  <th class="text-left">Oportunitate</th>
                  <th class="text-left">Partener</th>
                  <th class="text-left">Valoare</th>
                  <th class="text-left">Status</th>
                  <th class="text-left">Data</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(opp, index) in recentOpportunities" :key="index" class="clickable-row"
                  @click="goToOpportunity(opp.id)">
                  <td>
                    <div class="opp-title-cell">
                      <v-icon size="18" color="primary" class="mr-2">mdi-briefcase-variant</v-icon>
                      <span class="opp-name">{{ opp.title }}</span>
                    </div>
                  </td>
                  <td>{{ opp.company }}</td>
                  <td class="opp-value">{{ opp.value }} EUR</td>
                  <td>
                    <v-chip :color="getStatusColor(opp.status)" size="x-small" label>
                      {{ opp.status }}
                    </v-chip>
                  </td>
                  <td class="opp-date">{{ opp.dateFormatted }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Activities & Recent -->
    <v-row class="bottom-row">
      <!-- Recent Activities -->


      <!-- Quick Stats -->

    </v-row>
    <!-- Recent Opportunities Row -->

  </div>
</template>

<script lang="ts">

import { defineComponent } from 'vue'
import AppSettings from '@/settings/appSettings'
import JwtServices from '@/services/JwtServices'
import http from '@/settings/apiSettings'
import ODataStore from 'devextreme/data/odata/store'
import {
  DxChart,
  DxSeries,
  DxArgumentAxis,
  DxValueAxis,
  DxTooltip,
  DxLegend,
  DxFormat
} from 'devextreme-vue/chart'
import DxDateBox from 'devextreme-vue/date-box'

export default defineComponent({
  name: 'CRMDashboard',
  components: {
    DxChart,
    DxSeries,
    DxArgumentAxis,
    DxValueAxis,
    DxTooltip,
    DxLegend,
    DxFormat,
    DxDateBox
  },

  data() {
    return {
      APP_Color: AppSettings.APP_Color,

      // Data stores
      dsSales: null as any,
      dsContacts: null as any,
      dsActivitati: null as any,
      dsParteneri: null as any,
      dsStatus: null as any,
      dsCampanii: null as any,
      dsValute: null as any,
      eurRate: 1,
      realizedTotal: 0,
      realizedTotalTeam: 0,
      currentDeadlineStr: '',

      stats: [
        {
          id: 1,
          label: 'Total Oportunități',
          value: '0',
          change: '+0%',
          trend: 'up',
          icon: 'mdi-briefcase-variant',
          color: '#00BCD4',
          bgColor: 'rgba(0, 188, 212, 0.1)',
          route: '/crm/grid-oportunitati'
        },
        {
          id: 2,
          label: 'Total Activități',
          value: '0',
          change: '+0%',
          trend: 'up',
          icon: 'mdi-calendar-check',
          color: '#4CAF50',
          bgColor: 'rgba(76, 175, 80, 0.1)',
          route: '/crm/activitati'
        },
        {
          id: 3,
          label: 'Total Contacte',
          value: '0',
          change: '+0%',
          trend: 'up',
          icon: 'mdi-account-group',
          color: '#2196F3',
          bgColor: 'rgba(33, 150, 243, 0.1)',
          route: '/crm/contacte'
        },
        {
          id: 4,
          label: 'Total Parteneri',
          value: '0',
          change: '+0%',
          trend: 'up',
          icon: 'mdi-account-tie',
          color: '#FF9800',
          bgColor: 'rgba(255, 152, 0, 0.1)',
          route: '/crm/parteneri'
        },

        {
          id: 5,
          label: 'Target Planificat',
          value: '0',
          change: '+0%',
          trend: 'up',
          icon: 'mdi-target',
          color: '#ff5722',
          bgColor: 'rgba(255, 87, 34, 0.1)',
          route: '/crm/dashboard'
        },
        {
          id: 6,
          label: 'Target Realizat',
          value: '0',
          change: '+0%',
          trend: 'up',
          icon: 'mdi-trophy-outline',
          color: '#FFD700',
          bgColor: 'rgba(255, 215, 0, 0.1)',
          route: '/crm/grid-oportunitati'
        },
        {
          id: 7,
          label: 'Target Planificat pe Echipă',
          value: '0',
          change: '+0%',
          trend: 'up',
          icon: 'mdi-target-variant',
          color: '#9C27B0',
          bgColor: 'rgba(156, 39, 176, 0.1)',
          route: '/crm/dashboard'
        },
        {
          id: 8,
          label: 'Target General pe Echipă',
          value: '0',
          change: '+0%',
          trend: 'up',
          icon: 'mdi-account-group',
          color: '#3F51B5',
          bgColor: 'rgba(63, 81, 181, 0.1)',
          route: '/crm/dashboard'
        },

      ],

      pipelineStages: [
        { name: 'Prospectare', count: 0, value: '0', percentage: 0, color: '#4CAF50' },
        { name: 'Calificare', count: 0, value: '0', percentage: 0, color: '#2196F3' },
        { name: 'Propunere', count: 0, value: '0', percentage: 0, color: '#FF9800' },
        { name: 'Negociere', count: 0, value: '0', percentage: 0, color: '#ff5722' },
        { name: 'Închidere', count: 0, value: '0', percentage: 0, color: '#9C27B0' }
      ],

      topPerformers: [] as any[],

      recentActivities: [] as any[],

      quickStats: [
        { label: 'Apeluri Astăzi', value: '0', icon: 'mdi-phone', color: '#4CAF50', bgColor: 'rgba(76, 175, 80, 0.1)' },
        { label: 'Emailuri Trimise', value: '0', icon: 'mdi-email', color: '#2196F3', bgColor: 'rgba(33, 150, 243, 0.1)' },
        { label: 'Întâlniri Programate', value: '0', icon: 'mdi-calendar', color: '#FF9800', bgColor: 'rgba(255, 152, 0, 0.1)' },
        { label: 'Task-uri Pending', value: '0', icon: 'mdi-checkbox-marked-circle', color: '#9C27B0', bgColor: 'rgba(156, 39, 176, 0.1)' },
        { label: 'Oferte Active', value: '0', icon: 'mdi-file-document', color: '#ff5722', bgColor: 'rgba(255, 87, 34, 0.1)' },
        { label: 'CLIENȚI NOI', value: '0', icon: 'mdi-account-plus', color: '#00BCD4', bgColor: 'rgba(0, 188, 212, 0.1)' }
      ],

      recentOpportunities: [] as any[],
      monthlySalesData: [] as any[],

      menuItems: [
        { label: 'Dashboard', route: '/crm/dashboard', icon: 'mdi-view-dashboard' },
        { label: 'Parteneri', route: '/crm/parteneri', icon: 'mdi-account-tie' },
        { label: 'Calendar', route: '/crm/calendar-activitati', icon: 'mdi-calendar-month' },
        { label: 'Contacte', route: '/crm/contacte', icon: 'mdi-account-group' },
        { label: 'Oportunități', route: '/crm/grid-oportunitati', icon: 'mdi-briefcase' },
        { label: 'Activități', route: '/crm/activitati', icon: 'mdi-calendar-check' },
        { label: 'KPI', route: '/crm/kpi', icon: 'mdi-chart-bar' },
        // { label: 'Campanii', route: '/crm/campanii', icon: 'mdi-bullhorn-outline' },

      ],

      // Chart Filter State
      chartStartDate: new Date(new Date().getFullYear(), 0, 1),
      chartEndDate: new Date(new Date().getFullYear(), 11, 31),

      // Pipeline Filter State
      pipelineStartDate: new Date(new Date().getFullYear(), 0, 1),
      pipelineEndDate: new Date(new Date().getFullYear(), 11, 31),

      // Store all sales for re-filtering
      allSalesData: [] as any[],
    }
  },

  mounted() {
    this.initializeDataSources()
    this.loadDashboardData()
  },

  methods: {
    initializeDataSources() {
      // Initialize OData stores
      this.dsSales = new ODataStore({
        url: AppSettings.API_Url_ComercialOdata + 'CRM_Sales',
        key: 'ID',
        version: 4,
        beforeSend: (e) => {
          e.headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
          }
          e.params.$expand = "Account($select=Nume),Status($select=Nume),Valuta($select=Simbol)";
          //e.params.$filter = "Owner eq '" + JwtServices.GetUsername() + "'";
        },
      })

      this.dsContacts = new ODataStore({
        url: AppSettings.API_Url_ComercialOdata + 'CRM_Contacts',
        key: 'ID',
        version: 4,
        beforeSend: (e) => {
          e.headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
          }
        },
      })

      this.dsActivitati = new ODataStore({
        url: AppSettings.API_Url_ComercialOdata + 'CRM_Activitati',
        key: 'ID',
        version: 4,
        beforeSend: (e) => {
          e.headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
          }
          e.params.$expand = "Sale($select=ID,Titlu)";
          //e.params.$filter = "Owner eq '" + JwtServices.GetUsername() + "'";
        },
      })

      this.dsParteneri = new ODataStore({
        url: AppSettings.API_Url_ComercialOdata + 'PAR_Parteneri',
        key: 'ID',
        version: 4,
        beforeSend: (e) => {
          e.headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
          }
        },
      })

      this.dsStatus = new ODataStore({
        url: AppSettings.API_Url_ComercialOdata + 'CRM_StariOportunitati',
        key: 'ID',
        version: 4,
        beforeSend: (e) => {
          e.headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
          }
        },
      })

      this.dsCampanii = new ODataStore({
        url: AppSettings.API_Url_ComercialOdata + 'CRM_CampaniiMarketing',
        key: 'ID',
        version: 4,
        beforeSend: (e) => {
          e.headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
          }
        },
      })

      this.dsValute = new ODataStore({
        url: AppSettings.API_Url_ComercialOdata + 'ERP_Valute',
        key: 'Id',
        version: 4,
        beforeSend: (e) => {
          e.headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
          }
        },
      })
    },

    async fetchEurRate() {
      try {
        const store = new ODataStore({
          url: AppSettings.API_Url_ComercialOdata + 'ERP_CursValutar',
          key: 'Id',
          version: 4,
          beforeSend: (e) => {
            e.headers = {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt')
            };
          }
        });

        const result = await store.load({
          filter: ["Simbol", "=", "EUR"],
          sort: [{ selector: "Data", desc: true }],
          take: 1
        }) as any[];

        if (result && result.length > 0) {
          this.eurRate = result[0].Curs || 1;
          console.log("EUR Exchange Rate fetched:", this.eurRate);
        }
      } catch (error) {
        console.error("Error fetching EUR rate:", error);
      }
    },

    async loadDashboardData() {
      try {
        // Fetch exchange rate first
        await this.fetchEurRate();
        await this.fetchMyTarget();

        // Load opportunities and statuses data
        const [salesData, statusData] = await Promise.all([
          this.dsSales.load(),
          this.dsStatus.load({ sort: [{ selector: 'NrOrd', desc: false }] })
        ])

        this.allSalesData = salesData; // Store full dataset

        this.calculateOpportunitiesStats(salesData)
        const filteredSales = this.filterSalesByDate(salesData, this.pipelineStartDate, this.pipelineEndDate);
        this.calculatePipelineStages(filteredSales, statusData)
        this.calculateTopPerformers(salesData)
        this.calculateRecentOpportunities(salesData)
        this.calculateMonthlyForecast(salesData, statusData)

        // Load contacts data
        const contactsData = await this.dsContacts.load()
        this.calculateContactsStats(contactsData)

        // Load partners data
        const partnersData = await this.dsParteneri.load()
        this.calculatePartnersStats(partnersData)

        // Load activities data
        const activitiesData = await this.dsActivitati.load()
        this.calculateActivitiesStats(activitiesData)
        this.calculateRecentActivities(activitiesData)

        // Load campaigns data
        const campaignsData = await this.dsCampanii.load()
        this.calculateCampaignsStats(campaignsData)

        this.calculateQuickStats(activitiesData, salesData, contactsData)
      } catch (error) {
        console.error('Error loading dashboard data:', error)
      }
    },

    refreshChart() {
      this.refreshChartLogic();
    },

    async refreshChartLogic() {
      const statusData = await this.dsStatus.load({ sort: [{ selector: 'NrOrd', desc: false }] });
      this.calculateMonthlyForecast(this.allSalesData, statusData);
    },

    async refreshPipeline() {
      const statusData = await this.dsStatus.load({ sort: [{ selector: 'NrOrd', desc: false }] });
      const filteredSales = this.filterSalesByDate(this.allSalesData, this.pipelineStartDate, this.pipelineEndDate);
      this.calculatePipelineStages(filteredSales, statusData);
    },

    filterSalesByDate(data: any[], startDate: Date, endDate: Date) {
      return data.filter(item => {
        const dateStr = item.Data;
        if (!dateStr) return false;
        const date = new Date(dateStr);
        if (startDate && date < startDate) return false;
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (date > end) return false;
        }
        return true;
      });
    },

    calculateOpportunitiesStats(data: any[]) {
      // Total opportunities
      this.stats[0].value = data.length.toString()

      // Realized Target
      const currentUsername = JwtServices.GetUsername();
      let realizedTotalTeam = 0;
      let realizedTotalUser = 0;

      data.forEach(item => {
        const statusName = item.Status?.Nume || item.Status;
        if (statusName === 'Câștigat' || statusName === 'Castigat') {
          let val = (item.VenitEstimat || item.BugetEstimat || 0);
          if (item.Valuta?.Simbol === 'RON' || !item.Valuta?.Simbol) {
            val = val / this.eurRate;
          }

          realizedTotalTeam += val;
          if (item.Owner === currentUsername) {
            realizedTotalUser += val;
          }
        }
      });

      this.stats[5].value = this.formatCurrency(realizedTotalUser);
      this.stats[7].value = this.formatCurrency(realizedTotalTeam);

      // Store values for combined update in fetchMyTarget
      this.realizedTotal = realizedTotalUser;
      this.realizedTotalTeam = realizedTotalTeam;
      this.updateRealizedChange();
    },

    updateRealizedChange() {
      const realizedValue = this.realizedTotal || 0;
      const plannedStr = this.stats[4].value || '0';
      const plannedValueStr = plannedStr.split(' ')[0].replace(/\./g, '').replace(',', '.');
      let plannedValueNumeric = parseFloat(plannedValueStr);

      // Convert to EUR if Planned is in RON (since realizedValue is in EUR)
      if (plannedStr.toUpperCase().includes('RON')) {
        plannedValueNumeric = plannedValueNumeric / (this.eurRate || 1);
      }

      let changeStr = '';
      if (plannedValueNumeric > 0) {
        const percent = Math.round((realizedValue / plannedValueNumeric) * 100);
        changeStr = `${percent}% realizat`;
        this.stats[5].trend = percent >= 100 ? 'up' : 'neutral';
      }

      if (this.currentDeadlineStr) {
        changeStr += (changeStr ? ' | ' : '') + `Până la ${this.currentDeadlineStr}`;
      }

      if (changeStr) {
        this.stats[5].change = changeStr;
      }
    },

    calculateContactsStats(data: any[]) {
      this.stats[2].value = data.length.toString()
    },

    calculatePartnersStats(data: any[]) {
      this.stats[3].value = data.length.toString()
    },

    calculateActivitiesStats(data: any[]) {
      this.stats[1].value = data.length.toString();
    },

    calculateCampaignsStats(data: any[]) {
      // Not displayed in stats row anymore
    },

    async fetchMyTarget() {
      try {
        const username = JwtServices.GetUsername();
        if (!username) return;

        const safeUsername = username.replace(/'/g, "''");
        const today = new Date().toISOString();

        // 1. Găsim planul activ care este valid pentru data curentă
        const responseActive = await http.clientComercialOdata.get('CRM_Target', {
          params: {
            '$filter': `CurentActiv eq true and DataStart le ${today} and DataEnd ge ${today}`,
            '$select': 'ID,Denumire,DataEnd,DataStart,TargetGeneral',
            '$expand': 'Valuta($select=Simbol)',
            '$top': 1
          }
        });

        const activePlans = responseActive.data?.value || [];

        if (activePlans.length === 0) {
          this.stats[4].value = '0';
          this.stats[4].label = 'Target Planificat';
          return;
        }

        const activePlan = activePlans[0];
        const planID = activePlan.ID;
        const deadlineDate = activePlan.DataEnd;
        const teamTargetValue = activePlan.TargetGeneral || 0;
        const teamValuta = activePlan.Valuta?.Simbol || '';

        this.stats[6].value = teamTargetValue.toLocaleString('ro-RO') + (teamValuta ? ' ' + teamValuta : '');
        this.stats[7].change = ''; // Reset team achievement change text initially

        if (teamTargetValue > 0) {
          const teamPercent = Math.round((this.realizedTotalTeam / teamTargetValue) * 100);
          this.stats[6].change = `${teamPercent}% realizat`;
          this.stats[6].trend = teamPercent >= 100 ? 'up' : 'neutral';

          this.stats[7].change = `${teamPercent}% din targetul ${teamTargetValue.toLocaleString('ro-RO')}`;
          this.stats[7].trend = teamPercent >= 100 ? 'up' : 'neutral';
        }

        // 2. Găsim target-ul utilizatorului logat pentru planul găsit mai sus
        const responseUser = await http.clientComercialOdata.get('CRM_TargetPeUser', {
          params: {
            '$filter': `TargetID eq ${planID} and Utilizator eq '${safeUsername}'`,
            '$expand': 'Valuta($select=Simbol)',
            '$top': 1
          }
        });

        const userTargets = responseUser.data?.value || [];

        if (userTargets.length > 0) {
          const item = userTargets[0];

          let targetValue = 0;
          const rawVal = item.Target;

          if (typeof rawVal === 'number') {
            targetValue = rawVal;
          } else if (typeof rawVal === 'string') {
            targetValue = parseFloat(rawVal) || 0;
          }

          const valutaSimbol = item.Valuta?.Simbol || '';
          this.stats[4].value = targetValue.toLocaleString('ro-RO') + (valutaSimbol ? ' ' + valutaSimbol : '');
          this.stats[4].label = 'Target Planificat';

          if (deadlineDate) {
            const deadline = new Date(deadlineDate);
            this.currentDeadlineStr = deadline.toLocaleDateString('ro-RO');
          }

          this.updateRealizedChange();
        } else {
          this.stats[4].value = '0';
          this.stats[4].label = 'Target Planificat';
        }
      } catch (error) {
        console.error('Error fetching my target:', error);
        this.stats[4].value = '0';
        this.stats[4].label = 'Target Planificat';
      }
    },

    calculateTopPerformers(data: any[]) {
      // Group opportunities by Owner
      const performerMap: { [key: string]: { deals: number; value: number } } = {}

      data.forEach(item => {
        const owner = item.Owner || 'Neasignat'
        if (!performerMap[owner]) {
          performerMap[owner] = { deals: 0, value: 0 }
        }
        performerMap[owner].deals++
        let val = (item.VenitEstimat || item.BugetEstimat || 0);
        if (item.Valuta?.Simbol === 'RON' || !item.Valuta?.Simbol) {
          val = val / this.eurRate;
        }
        performerMap[owner].value += val;
      })

      // Convert to array and sort by deals count
      const performers = Object.entries(performerMap)
        .map(([name, stats]) => ({
          name,
          deals: stats.deals,
          value: this.formatCurrency(stats.value),
          color: '#2196F3' // Default color
        }))
        .sort((a, b) => b.deals - a.deals)
        .slice(0, 5) // Top 5

      // Assign colors based on rank
      const colors = ['#FFD700', '#C0C0C0', '#CD7F32', '#ff5722', '#2196F3']
      performers.forEach((performer, index) => {
        performer.color = colors[index] || '#2196F3'
      })

      this.topPerformers = performers
    },

    calculateRecentOpportunities(data: any[]) {
      // Sort by date (assuming Data field exists) or revert to newest first
      const opportunities = [...data]
        .filter(item => item.Data)
        .sort((a, b) => new Date(b.Data).getTime() - new Date(a.Data).getTime())
        .slice(0, 5)
        .map(item => ({
          id: item.ID,
          title: item.Titlu || 'Oportunitate fără titlu',
          company: item.Account?.Nume || '-',
          value: (() => {
            let val = (item.VenitEstimat || item.BugetEstimat || 0);
            if (item.Valuta?.Simbol === 'RON' || !item.Valuta?.Simbol) {
              val = val / this.eurRate;
            }
            return Math.round(val).toLocaleString('ro-RO');
          })(),
          status: item.Status?.Nume || item.Status || '-',
          dateFormatted: item.Data ? new Date(item.Data).toLocaleDateString('ro-RO') : '-',
          color: '#2196F3'
        }))

      this.recentOpportunities = opportunities
    },

    goToOpportunity(opportunityId: string) {
      this.$router.push({
        name: 'GridOportunitati',
        query: { opportunityId: opportunityId }
      });
    },

    getStatusColor(status: string): string {
      const statusMap: { [key: string]: string } = {
        'Câștigat': 'success',
        'Închis': 'error',
        'Negociere': 'warning',
        'Nou': 'info',
        'Calificare': 'primary'
      }
      return statusMap[status] || 'grey'
    },

    calculateRecentActivities(data: any[]) {
      // Sort by date and take the most recent 5
      const activities = data
        .filter(item => item.Data) // Only items with dates
        .sort((a, b) => new Date(b.Data).getTime() - new Date(a.Data).getTime())
        .slice(0, 5)
        .map(item => {
          const activityDate = new Date(item.Data)
          const now = new Date()
          const diffMs = now.getTime() - activityDate.getTime()
          const isFuture = diffMs < 0;
          const absDiffMs = Math.abs(diffMs);

          const diffMins = Math.floor(absDiffMs / 60000)
          const diffHours = Math.floor(absDiffMs / 3600000)
          const diffDays = Math.floor(absDiffMs / 86400000)

          let timeText = ''

          if (diffMins < 1) {
            timeText = 'Chiar acum';
          } else if (diffMins < 60) {
            timeText = isFuture ? `Peste ${diffMins} min` : `Acum ${diffMins} min`
          } else if (diffHours < 24) {
            timeText = isFuture ? `Peste ${diffHours}h` : `Acum ${diffHours}h`
          } else {
            timeText = isFuture ? `Peste ${diffDays} zile` : `Acum ${diffDays} zile`
          }

          // Determine color and tag based on activity type
          let color = '#2196F3'
          let tag = '' /* Removed default 'Activitate' */
          let tagColor = 'primary'

          const type = (item.Tip || '').toLowerCase()
          if (type.includes('intalnire') || type.includes('meeting')) {
            color = '#4CAF50'
            tag = 'Întâlnire'
            tagColor = 'success'
          } else if (type.includes('apel') || type.includes('call')) {
            color = '#FF9800'
            tag = 'Apel'
            tagColor = 'warning'
          } else if (type.includes('email')) {
            color = '#9C27B0'
            tag = 'Email'
            tagColor = 'purple'
          } else if (type.includes('task')) {
            color = '#00BCD4'
            tag = 'Task'
            tagColor = 'info'
          }

          /* If tag is empty, set a default icon color but no text tag */
          if (!tag) {
            tagColor = 'grey'
          }

          return {
            id: item.ID,
            description: item.Descriere || item.Locatie || '-',
            time: timeText,
            opportunity: item.Sale?.Titlu,
            opportunityId: item.Sale?.ID,
            color,
            tag,
            tagColor
          }
        })

      this.recentActivities = activities
    },

    calculateQuickStats(activitiesData: any[], salesData: any[], contactsData: any[]) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const thisWeekStart = new Date(today)
      thisWeekStart.setDate(today.getDate() - today.getDay())

      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)

      // Apeluri Astăzi
      const callsToday = activitiesData.filter(item => {
        const itemDate = new Date(item.Data)
        return itemDate >= today && (item.Tip || '').toLowerCase().includes('apel')
      }).length
      this.quickStats[0].value = callsToday.toString()

      // Emailuri Trimise (săptămâna aceasta)
      const emailsThisWeek = activitiesData.filter(item => {
        const itemDate = new Date(item.Data)
        return itemDate >= thisWeekStart && (item.Tip || '').toLowerCase().includes('email')
      }).length
      this.quickStats[1].value = emailsThisWeek.toString()

      // Întâlniri Programate (viitoare)
      const upcomingMeetings = activitiesData.filter(item => {
        const itemDate = new Date(item.Data)
        return itemDate > new Date() && (item.Tip || '').toLowerCase().includes('intalnire')
      }).length
      this.quickStats[2].value = upcomingMeetings.toString()

      // Task-uri Pending
      const pendingTasks = activitiesData.filter(item => {
        return (item.Status || '').toLowerCase() !== 'completat' &&
          (item.Tip || '').toLowerCase().includes('task')
      }).length
      this.quickStats[3].value = pendingTasks.toString()

      // Oferte Active
      const activeOffers = salesData.filter(item => {
        const statusName = item.Status?.Nume || item.Status;
        return statusName !== 'Închis' && statusName !== 'Câștigat'
      }).length
      this.quickStats[4].value = activeOffers.toString()

      // Clienți Noi (luna aceasta)
      const newContactsThisMonth = contactsData.filter(item => {
        if (!item.DataCreare) return false
        const itemDate = new Date(item.DataCreare)
        return itemDate >= thisMonthStart
      }).length
      this.quickStats[5].value = newContactsThisMonth.toString()
    },

    calculatePipelineStages(salesData: any[], statusData: any[]) {
      // Colors for stages
      const colors = ['#4CAF50', '#2196F3', '#FF9800', '#ff5722', '#9C27B0', '#00BCD4', '#009688', '#673AB7']

      // Map dynamic statuses to pipeline stages
      this.pipelineStages = statusData.map((status, index) => {
        const stageOpps = salesData.filter(opp => {
          // Check for status compatibility (expanded object or ID)
          const oppStatusID = opp.StatusID || opp.Status?.ID
          return oppStatusID === status.ID || opp.Status === status.Nume || (opp.Status?.Nume === status.Nume)
        })

        const totalValue = stageOpps.reduce((sum, opp) => {
          let val = (opp.VenitEstimat || opp.BugetEstimat || 0);
          if (opp.Valuta?.Simbol === 'RON' || !opp.Valuta?.Simbol) {
            val = val / this.eurRate;
          }
          return sum + val;
        }, 0)

        return {
          name: status.Nume,
          count: stageOpps.length,
          value: Math.round(totalValue).toLocaleString('ro-RO'),
          percentage: 0, // Will calculate below
          color: colors[index % colors.length]
        }
      })

      // Calculate percentages based on counts
      const maxCount = Math.max(...this.pipelineStages.map(s => s.count), 1)
      this.pipelineStages.forEach(stage => {
        stage.percentage = (stage.count / maxCount) * 100
      })
    },

    calculateMonthlyForecast(data: any[], statusData: any[]) {
      const monthNames = [
        'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
        'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
      ];

      const forecastMap: { [key: string]: number } = {};

      // Filter data for the chart period
      const filteredData = data.filter(item => {
        const dateStr = item.DataInchidereEstimat || item.Data; // Use same date logic as sorting
        if (!dateStr) return false;
        const date = new Date(dateStr);
        if (this.chartStartDate && date < this.chartStartDate) return false;
        // End Date inclusive
        if (this.chartEndDate) {
          const end = new Date(this.chartEndDate);
          end.setHours(23, 59, 59, 999);
          if (date > end) return false;
        }
        return true;
      });

      filteredData.forEach(item => {
        // Use DataInchidereEstimat (Estimated Close Date) for forecast
        // Fallback to Data (Creation Date) if estimated is missing
        const dateStr = item.DataInchidereEstimat || item.Data;
        if (!dateStr) return;

        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return;

        // Key: "YYYY-MM" for sorting
        const year = date.getFullYear();
        const monthIndex = date.getMonth();
        const key = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

        if (!forecastMap[key]) {
          forecastMap[key] = 0;
        }

        // Calculate weighted budget: Budget * (Status Probability / 100)
        // Find status in statusData to get Pondere
        let probability = 0;

        // Try to find status by ID or Name
        const statusId = item.StatusID || item.Status?.ID;
        const statusName = item.Status?.Nume || item.Status;

        const matchedStatus = statusData.find(s =>
          (statusId && s.ID === statusId) ||
          (statusName && s.Nume === statusName)
        );

        if (matchedStatus && typeof matchedStatus.Pondere === 'number') {
          probability = matchedStatus.Pondere;
        }

        let budget = item.BugetEstimat || 0;
        if (item.Valuta?.Simbol === 'RON' || !item.Valuta?.Simbol) {
          budget = budget / this.eurRate;
        }
        const weightedValue = budget * (probability / 100);
        forecastMap[key] += weightedValue;
      });

      // Convert to array and sort by date key
      this.monthlySalesData = Object.entries(forecastMap)
        .map(([key, value]) => {
          const [year, month] = key.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, 1);
          return {
            month: `${monthNames[date.getMonth()]} ${year}`,
            value: value,
            sortKey: key
          };
        })
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    },

    formatCurrency(value: number): string {
      return new Intl.NumberFormat('ro-RO', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    },

    refreshData() {
      this.loadDashboardData()
      console.log('Refreshing dashboard data...')
    },

    isActiveRoute(route: string): boolean {
      return this.$route.path === route
    }
  }
})
</script>

<style scoped>
.crm-dashboard {
  padding: 2rem;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
  min-height: 100vh;
}

/* ========== HEADER ========== */
.dashboard-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.dashboard-subtitle {
  font-size: 1rem;
  color: #718096;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

/* ========== NAVIGATION MENU ========== */
.nav-menu-card {
  border-radius: 16px !important;
  margin-bottom: 2rem;
  overflow: hidden;
}

.nav-menu {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
  background: white;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  text-decoration: none;
  color: #718096;
  font-weight: 500;
  font-size: 0.9375rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-item:hover {
  background: #f7fafc;
  color: #1a202c;
}

.nav-item.active {
  background: linear-gradient(135deg, rgba(255, 87, 34, 0.1) 0%, rgba(255, 87, 34, 0.05) 100%);
  color: var(--v-theme-primary);
  font-weight: 600;
}

.nav-label {
  font-size: 0.9375rem;
}

/* ========== STATS CARDS ========== */
.stats-row {
  margin-bottom: 1.5rem;
}

.stat-card {
  border-radius: 14px !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  background: white !important;
  height: 100%;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
  border-color: var(--v-theme-primary) !important;
}

.stat-card-inner {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.stat-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.stat-icon-mini {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.stat-card:hover .stat-icon-mini {
  transform: scale(1.1) rotate(-5deg);
}

.stat-trend-mini {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
}

.stat-trend-mini.up {
  color: #4CAF50;
  background: rgba(76, 175, 80, 0.1);
}

.stat-trend-mini.down {
  color: #f44336;
  background: rgba(244, 67, 54, 0.1);
}

.stat-trend-mini.neutral {
  color: #718096;
  background: rgba(113, 128, 150, 0.1);
}

.stat-main-content {
  flex: 1;
}

.stat-value-mini {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1a202c;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.stat-label-mini {
  font-size: 0.75rem;
  font-weight: 600;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-footer-mini {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #edf2f7;
  font-size: 0.7rem;
  color: #718096;
  font-weight: 500;
  display: flex;
  align-items: center;
}

/* ========== CHARTS ========== */
.charts-row {
  margin-bottom: 1.5rem;
}

.chart-card {
  border-radius: 16px !important;
  height: 100%;
}

.card-title {
  display: flex;
  align-items: center;
  font-weight: 600 !important;
  font-size: 1.125rem !important;
  padding: 1.5rem !important;
  border-bottom: 1px solid #e0e0e0;
}

/* Pipeline */

/* Premium Chart Filters */
.chart-filters {
  background: #ffffff;
  /* Slate 100 */
  padding: 4px;
  border-radius: 10px;
  border: 1px solid #ffffff;
}

.premium-date-box .dx-texteditor-input {
  font-family: inherit !important;
  font-weight: 500 !important;
  font-size: 0.85rem !important;
  text-align: center !important;
}

.premium-date-box.dx-editor-outlined {
  background: white !important;
  border: 1px solid transparent !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.premium-date-box.dx-editor-outlined.dx-state-hover {
  background: #fff !important;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1) !important;
}

.premium-date-box.dx-editor-outlined.dx-state-focused {
  border-color: #3b82f6 !important;
  /* Blue 500 */
}

.filter-separator {
  color: #94a3b8;
  font-weight: bold;
  margin: 0 6px;
}

.pipeline-stages {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.pipeline-stage {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stage-name {
  font-weight: 600;
  color: #1a202c;
  font-size: 0.9375rem;
}

.stage-count {
  background: #f0f0f0;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #666;
}

.stage-bar {
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.stage-progress {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 4px;
}

.stage-value {
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
}

/* Performers */
.performers-list {
  padding: 0 !important;
}

.performer-item {
  padding: 0.75rem 0 !important;
  border-bottom: 1px solid #f0f0f0;
}

.performer-item:last-child {
  border-bottom: none;
}

.performer-rank {
  color: white;
  font-weight: 700;
  font-size: 1rem;
}

.performer-name {
  font-weight: 600 !important;
  font-size: 0.9375rem !important;
}

.performer-deals {
  font-size: 0.875rem !important;
  color: #718096 !important;
}

/* ========== ACTIVITIES ========== */
.activity-card {
  border-radius: 16px !important;
  height: 100%;
}

.activity-timeline {
  padding: 1.5rem;
}

.activity-content {
  padding-bottom: 1rem;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.activity-title {
  font-weight: 600;
  color: #1a202c;
  font-size: 0.9375rem;
}

.activity-time {
  font-size: 0.75rem;
  color: #a0aec0;
}

.activity-description {
  font-size: 0.875rem;
  color: #718096;
  line-height: 1.4;
}

/* ========== QUICK STATS ========== */
.quick-stats-card {
  border-radius: 16px !important;
  height: 100%;
}

.quick-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.quick-stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #fafafa;
  border-radius: 12px;
  transition: all 0.2s;
}

.quick-stat-item:hover {
  background: #f0f0f0;
  transform: translateY(-2px);
}

.quick-stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quick-stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1;
}

.quick-stat-label {
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 960px) {
  .crm-dashboard {
    padding: 1rem;
  }

  .dashboard-title {
    font-size: 1.5rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .nav-menu {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .nav-menu::-webkit-scrollbar {
    height: 4px;
  }

  .nav-menu::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 4px;
  }

  .quick-stats-grid {
    grid-template-columns: 1fr;
  }
}

/* ========== DASHBOARD TABLE ========== */
.dashboard-table {
  background: white !important;
}

.dashboard-table th {
  font-size: 0.75rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #718096 !important;
  font-weight: 600 !important;
  padding: 1rem 1.5rem !important;
  border-bottom: 2px solid #edf2f7 !important;
}

.dashboard-table td {
  padding: 1rem 1.5rem !important;
  font-size: 0.875rem !important;
  color: #2d3748 !important;
  border-bottom: 1px solid #edf2f7 !important;
}

.dashboard-table tr:hover td {
  background-color: #f8fafc;
}

.opp-title-cell {
  display: flex;
  align-items: center;
}

.opp-name {
  font-weight: 600;
  color: #1a202c;
}

.opp-value {
  font-weight: 700;
  color: #2c7a7b;
}

.opp-date {
  color: #718096;
  font-size: 0.8125rem;
}

@media (max-width: 600px) {
  .stat-card {
    flex-direction: column;
    text-align: center;
  }

  .stat-icon-wrapper {
    margin: 0 auto;
  }
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.clickable-row:hover {
  background-color: #f5f5f5 !important;
}

.activity-opp-link {
  font-size: 0.75rem;
  color: #2b6cb0;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.activity-opp-link:hover {
  color: #1a365d;
  text-decoration: underline;
}

.clickable-activity {
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s;
  margin: -8px;
  /* Offset the padding for alignment */
}

.clickable-activity:hover {
  background-color: #f8fafc;
}

.clickable-activity:hover .activity-title {
  color: #2b6cb0;
}
</style>
